import {
  GQCanonicalDatasetKind,
  GQWasteEmissionsMethodology,
} from './generated/graphql';
import { getKindIsFacilitiesDataset } from './measurement/BuildingUtils';
import assertNever from './utils/assertNever';
import isIncludedIn from './utils/isIncludedIn';

export const CANONICAL_DATASET_BEA_CODE_MAPPING = {
  CloudUsage: ['naics_518200'],
  CryptoTransactions: ['naics_518200'],
  EnergyAttributeCertificatesPurchased: ['naics_221100'],
  Flights: ['naics_481000'],
  PrivateFlights: ['naics_481000'],
  Hotels: ['naics_721000'],
  Rails: ['naics_482000'],
  Refrigerants: ['naics_333415'],
  Waste: ['naics_562000'],
  DataCenters: ['naics_221100', 'naics_518200'],
  Food: ['naics_722110', 'naics_722211', 'naics_722a00'],
  Vehicles: [
    'naics_324110',
    'naics_447000',
    'naics_485000',
    'naics_532100',
    'naics_221100',
  ],
  WaterUsage: ['naics_221300', 'naics_312110'],
  UpstreamLogistics: [
    'naics_481000',
    'naics_482000',
    'naics_483000',
    'naics_484000',
    'naics_491000',
    'naics_492000',
  ],
  Employees: [
    'naics_562000',
    'naics_324110',
    'naics_447000',
    'naics_485000',
    'naics_532100',
  ],
  Packaging: [
    'naics_326130',
    'naics_326140',
    'naics_326150',
    'naics_326160',
    'naics_326190',
    'naics_326220',
    'naics_326290',
    'naics_327100',
    'naics_327200',
    'naics_331200',
    'naics_33131b',
    'naics_331420',
    'naics_33211b',
  ],
} satisfies Partial<{
  [key in GQCanonicalDatasetKind]: Array<string>;
}>;

export type BeaCodeMappedCanonicalDatasetKind =
  keyof typeof CANONICAL_DATASET_BEA_CODE_MAPPING;

export function isBeaCodeMappedCanonicalDatasetKind(
  kind: GQCanonicalDatasetKind
): kind is BeaCodeMappedCanonicalDatasetKind {
  return kind in CANONICAL_DATASET_BEA_CODE_MAPPING;
}

/**
 * We're defining some groups for these datasets as part of the milestones
 * project as we prototype. This may eventually want to graduate to a model if
 * we get traction here.
 */
export enum CanonicalDatasetGroup {
  Facilities = 'Facilities',
  BusinessData = 'BusinessData',
  Cloud = 'Cloud',
  Custom = 'Custom',
  DataCenters = 'DataCenters',
  Employees = 'Employees',
  Financials = 'Financials',
  Logistics = 'Logistics',
  Products = 'Products',
  Refrigerants = 'Refrigerants',
  Sites = 'Sites',
  Substances = 'Substances',
  Travel = 'Travel',
  Waste = 'Waste',
  Water = 'Water',
}

export type CanonicalDatasetGroupOrAcrossDatasets =
  | CanonicalDatasetGroup
  | 'AcrossDatasets';

/**
 * These are the canonical datasets kinds that are allowed for Essential customers
 * and Standard or above.
 */

export const CANONICAL_DATASETS_FOR_ESSENTIAL = [
  GQCanonicalDatasetKind.Buildings,
  GQCanonicalDatasetKind.Utilities,
  GQCanonicalDatasetKind.Flights,
  GQCanonicalDatasetKind.Hotels,
  GQCanonicalDatasetKind.Employees,
  GQCanonicalDatasetKind.Revenue,
  GQCanonicalDatasetKind.PrivateFlights,
  GQCanonicalDatasetKind.EmployeeTravelExpenses,
];

export const CANONICAL_DATASETS_FOR_STANDARD = [
  ...CANONICAL_DATASETS_FOR_ESSENTIAL,
  GQCanonicalDatasetKind.OperatingExpenses,
  GQCanonicalDatasetKind.CapitalExpenses,
  GQCanonicalDatasetKind.CostOfRevenue,
  GQCanonicalDatasetKind.CloudCosts,
  GQCanonicalDatasetKind.CloudUsage,
  GQCanonicalDatasetKind.DataCenters,
  GQCanonicalDatasetKind.Rails,
  GQCanonicalDatasetKind.Vehicles,
  GQCanonicalDatasetKind.UpstreamLogistics,
  GQCanonicalDatasetKind.DownstreamLogistics,
  // CG/Apparel datasets
  GQCanonicalDatasetKind.EndOfLifeTreatmentOfSoldProducts,
  GQCanonicalDatasetKind.Packaging,
  GQCanonicalDatasetKind.MaterialAggregate, // this is Products & Materials TODO(tammy): Rename this CanonicalDatasetKind
  GQCanonicalDatasetKind.SupplierFactoriesTier1,
  GQCanonicalDatasetKind.SupplierUpstreamLogistics,
  GQCanonicalDatasetKind.UseOfSoldProductsFuels, // this is Use of Sold Products TODO(tammy): Rename this CanonicalDatasetKind
  // CSRD datasets
  GQCanonicalDatasetKind.SoldProductCircularity,
];

export const CANONICAL_DATASETS_FOR_ENTERPRISE = [
  ...CANONICAL_DATASETS_FOR_STANDARD,
  GQCanonicalDatasetKind.CorporateInvestments,
];

/**
 * Set some groupings for the canonical dataset kinds. Typescript will force
 * this to be exhaustive on CanonicalDatasetKind.
 */
export const CANONICAL_DATASET_GROUPING: {
  [key in GQCanonicalDatasetKind]: CanonicalDatasetGroup;
} = {
  BillOfMaterials: CanonicalDatasetGroup.Products,
  Buildings: CanonicalDatasetGroup.Facilities,
  CapitalExpenses: CanonicalDatasetGroup.Financials,
  CareInstructions: CanonicalDatasetGroup.Products,
  ChartOfAccounts: CanonicalDatasetGroup.Financials,
  CloudCosts: CanonicalDatasetGroup.Cloud,
  CloudUsage: CanonicalDatasetGroup.Cloud,
  CorporateInvestments: CanonicalDatasetGroup.Financials,
  CostOfRevenue: CanonicalDatasetGroup.Financials,
  CryptoHoldings: CanonicalDatasetGroup.Financials,
  CryptoTransactions: CanonicalDatasetGroup.Financials,
  CustomEmissionsFactor: CanonicalDatasetGroup.Custom,
  DataCenters: CanonicalDatasetGroup.DataCenters,
  DownstreamLogistics: CanonicalDatasetGroup.Logistics,
  EmployeeTravelExpenses: CanonicalDatasetGroup.Travel,
  Employees: CanonicalDatasetGroup.Employees,
  EndOfLifeTreatmentOfSoldProducts: CanonicalDatasetGroup.Products,
  EnergyAttributeCertificatesPurchased: CanonicalDatasetGroup.Facilities,
  Factories: CanonicalDatasetGroup.Products,
  Financials: CanonicalDatasetGroup.Financials,
  Flights: CanonicalDatasetGroup.Travel,
  Food: CanonicalDatasetGroup.Products,
  HistoricalResults: CanonicalDatasetGroup.BusinessData,
  Hotels: CanonicalDatasetGroup.Travel,
  MaterialAggregate: CanonicalDatasetGroup.Products,
  MaterialContents: CanonicalDatasetGroup.Products,
  PrivateFlights: CanonicalDatasetGroup.Travel,
  OperatingExpenses: CanonicalDatasetGroup.Financials,
  Packaging: CanonicalDatasetGroup.Products,
  ProductsPurchased: CanonicalDatasetGroup.Products,
  ProductsSold: CanonicalDatasetGroup.Products,
  Refrigerants: CanonicalDatasetGroup.Refrigerants,
  Rails: CanonicalDatasetGroup.Travel,
  Revenue: CanonicalDatasetGroup.Financials,
  SoldProductCircularity: CanonicalDatasetGroup.Products,
  Sites: CanonicalDatasetGroup.Sites,
  SupplierFactoriesTier1: CanonicalDatasetGroup.Products,
  SupplierUpstreamLogistics: CanonicalDatasetGroup.Products,
  TextileProductSales: CanonicalDatasetGroup.Products,
  UpstreamLogistics: CanonicalDatasetGroup.Logistics,
  UseOfGoods: CanonicalDatasetGroup.Products,
  UseOfSoldProductsRefrigerants: CanonicalDatasetGroup.Products,
  UseOfSoldProductsFuels: CanonicalDatasetGroup.Products,
  UseOfSoldProductsVehicles: CanonicalDatasetGroup.Products,
  UseOfSoldProductsCommonRetail: CanonicalDatasetGroup.Products,
  UsePhaseOfGoods: CanonicalDatasetGroup.Products,
  Utilities: CanonicalDatasetGroup.Facilities,
  Substances: CanonicalDatasetGroup.Substances,
  Vehicles: CanonicalDatasetGroup.Travel,
  WeightOfGoodsPurchased: CanonicalDatasetGroup.Products,
  Waste: CanonicalDatasetGroup.Waste,
  WaterUsage: CanonicalDatasetGroup.Water,
};

export const FINANCIALS_RELATED = [
  GQCanonicalDatasetKind.CapitalExpenses,
  GQCanonicalDatasetKind.CostOfRevenue,
  GQCanonicalDatasetKind.Financials,
  GQCanonicalDatasetKind.OperatingExpenses,
  GQCanonicalDatasetKind.EmployeeTravelExpenses,
] as const;

// A list of datasets allowed for plans that do not have vendor-specific
// information, so vendor mapping is not relevant
const SUPPLIERS_EXCLUDED = [
  GQCanonicalDatasetKind.Buildings,
  GQCanonicalDatasetKind.Utilities,
  GQCanonicalDatasetKind.EndOfLifeTreatmentOfSoldProducts,
  GQCanonicalDatasetKind.UseOfSoldProductsFuels,
  GQCanonicalDatasetKind.CorporateInvestments,
  GQCanonicalDatasetKind.Employees,
  GQCanonicalDatasetKind.Revenue,

  // Exclude CloudCosts and CloudUsage becaues supplier mapping is implied via
  // the data ingestion schema and subsequent mapping in CTS SQL. See SUP-5800
  GQCanonicalDatasetKind.CloudCosts,
  GQCanonicalDatasetKind.CloudUsage,
] as const;

// 2024-02-15: restrict supplier mapping to financials for now as value-mapping
// address underlying reliability. The list should usually be the full set of
// datasets excluding the ones in SUPPLIERS_EXCLUDED. See SUP-5910
const SUPPLIERS_INCLUDED = [
  ...FINANCIALS_RELATED,
  // Supplier is required on tier 1 factory data.
  GQCanonicalDatasetKind.SupplierFactoriesTier1,
] as const;

export type SuppliersCanonicalDatasetKind = Exclude<
  (typeof SUPPLIERS_INCLUDED)[number],
  (typeof SUPPLIERS_EXCLUDED)[number]
>;

// A list of datasets that require vendor-specific information, so vendor mapping is required
const SUPPLIERS_REQUIRED_TYPED: Array<SuppliersCanonicalDatasetKind> = [
  GQCanonicalDatasetKind.SupplierFactoriesTier1,
] as const;

export const SUPPLIERS_REQUIRED: Array<GQCanonicalDatasetKind> =
  SUPPLIERS_REQUIRED_TYPED;

// A list of datasets that will be associated with a footprint but are non-emissive.
export const NON_EMISSIVE_DATASET_KINDS: Array<GQCanonicalDatasetKind> = [
  // Used to generated intensity denominators.
  GQCanonicalDatasetKind.Revenue,
  // Not used to generate intensity denominators.
  GQCanonicalDatasetKind.Sites,
  // TODO add dataset kind for monthly active users
  GQCanonicalDatasetKind.SoldProductCircularity,
];

export function getCanonicalDatasetGroup(
  kind: GQCanonicalDatasetKind | null
): CanonicalDatasetGroup {
  if (kind == null) {
    return CanonicalDatasetGroup.BusinessData;
  }
  return CANONICAL_DATASET_GROUPING[kind];
}

export const FINANCIALS_RELATED_CANONICAL_DATASET_KINDS = [
  GQCanonicalDatasetKind.OperatingExpenses,
  GQCanonicalDatasetKind.CapitalExpenses,
  GQCanonicalDatasetKind.CostOfRevenue,
  GQCanonicalDatasetKind.Financials,
  GQCanonicalDatasetKind.ChartOfAccounts,
];

export function isPdfAllowedForDataset(
  canonicalDatasetKind?: GQCanonicalDatasetKind | null
): boolean {
  if (!canonicalDatasetKind) {
    return true;
  }

  return getKindIsFacilitiesDataset(canonicalDatasetKind);
}

// TODO: is this valid anymore?
export function isAutoBartAvailable(
  canonicalDatasetKind?: GQCanonicalDatasetKind | null
): boolean {
  return (
    canonicalDatasetKind === GQCanonicalDatasetKind.Buildings ||
    canonicalDatasetKind === GQCanonicalDatasetKind.Employees
  );
}

export function isFinancialsRelated(
  kind?: GQCanonicalDatasetKind | null
): kind is (typeof FINANCIALS_RELATED)[number] {
  if (!kind) {
    return false;
  }
  return isIncludedIn(kind, FINANCIALS_RELATED);
}

export const CANONICAL_DATASET_KIND_TO_WASTE_EMISSIONS_METHODOLOGY: Partial<
  Record<GQCanonicalDatasetKind, GQWasteEmissionsMethodology>
> = {
  // In preferential order -- most accurate to least accurate
  [GQCanonicalDatasetKind.Waste]: GQWasteEmissionsMethodology.Waste,
  [GQCanonicalDatasetKind.Utilities]: GQWasteEmissionsMethodology.BuildingWaste,
  [GQCanonicalDatasetKind.Employees]: GQWasteEmissionsMethodology.Employees,
};

/**
 * Get BAT types for a canonical dataset
 */
export function getRecommendedActivityTypesForCanonicalDataset(
  canonicalDatasetKind: GQCanonicalDatasetKind
): Array<string> {
  switch (canonicalDatasetKind) {
    case GQCanonicalDatasetKind.Buildings:
      return ['building_occupation'];
    case GQCanonicalDatasetKind.CloudCosts:
      return ['cloud_spend'];
    case GQCanonicalDatasetKind.CloudUsage:
      return ['cloud_compute_usage'];
    case GQCanonicalDatasetKind.ChartOfAccounts:
      return [];
    case GQCanonicalDatasetKind.CorporateInvestments:
      return ['corporate_315_investments'];
    case GQCanonicalDatasetKind.CostOfRevenue:
    case GQCanonicalDatasetKind.OperatingExpenses:
    case GQCanonicalDatasetKind.CapitalExpenses:
    case GQCanonicalDatasetKind.Financials:
    case GQCanonicalDatasetKind.EmployeeTravelExpenses:
      // - expenditure_unmapped is the best recommendation for custom SQL in product
      // - expenditure should only be recommended on legacy measurement before Value Mappings
      //   which is rare and can be omitted for now
      // - expenditure_mapped is a decent recommendation for CTS admin tooling if you're writing
      //   a CTS that directly produces mapped data. We could add that to this list if there's
      //   a way to make this list different the use case, to ensure that custom SQL in product
      //   is always guided towards expenditure_unmapped
      return ['expenditure_unmapped'];
    case GQCanonicalDatasetKind.Flights:
      return ['travel_air'];
    case GQCanonicalDatasetKind.PrivateFlights:
      return ['travel_private_jets', 'fuel_combustion'];
    case GQCanonicalDatasetKind.HistoricalResults:
      return ['historical_emissions'];
    case GQCanonicalDatasetKind.Hotels:
      return ['lodging'];
    case GQCanonicalDatasetKind.Utilities:
      return [
        'electricity_consumption',
        'building_stationary_combustion',
        'electricity_consumption_datacenter',
        'refrigerants',
      ];
    case GQCanonicalDatasetKind.DataCenters:
      return ['electricity_consumption_datacenter'];
    case GQCanonicalDatasetKind.Employees:
      return ['employees'];
    case GQCanonicalDatasetKind.UpstreamLogistics:
    case GQCanonicalDatasetKind.DownstreamLogistics:
      return ['logistics'];
    case GQCanonicalDatasetKind.Rails:
      return ['travel_rail'];
    case GQCanonicalDatasetKind.Refrigerants:
      return ['refrigerants'];
    case GQCanonicalDatasetKind.Revenue:
      return ['intensity_denominator_monetary'];
    case GQCanonicalDatasetKind.Waste:
      return ['waste'];
    case GQCanonicalDatasetKind.WaterUsage:
      return ['water_usage'];
    case GQCanonicalDatasetKind.UseOfSoldProductsCommonRetail:
      return ['sold_retail_good'];
    case GQCanonicalDatasetKind.UseOfSoldProductsVehicles:
      return ['sold_vehicle'];
    case GQCanonicalDatasetKind.UseOfSoldProductsRefrigerants:
      return ['sold_refrigerant'];
    case GQCanonicalDatasetKind.UseOfSoldProductsFuels:
      return ['lifetime_fuel_and_electricity_use_of_sold_products'];
    case GQCanonicalDatasetKind.BillOfMaterials:
    case GQCanonicalDatasetKind.MaterialContents:
      return ['consumer_goods_material_assessment'];
    case GQCanonicalDatasetKind.CareInstructions:
      return [
        'consumer_goods_textile_product_use_phase',
        'consumer_goods_material_assessment',
      ];
    case GQCanonicalDatasetKind.MaterialAggregate:
      return ['waste', 'consumer_goods_material_assessment'];
    case GQCanonicalDatasetKind.ProductsPurchased:
      return ['consumer_goods_textile_product_use_phase'];
    case GQCanonicalDatasetKind.ProductsSold:
      return [
        'waste',
        'consumer_goods_material_assessment',
        'consumer_goods_textile_product_use_phase',
      ];
    case GQCanonicalDatasetKind.CustomEmissionsFactor:
      return ['activity_with_custom_ef'];
    case GQCanonicalDatasetKind.CryptoHoldings:
      return ['crypto_holdings'];
    case GQCanonicalDatasetKind.CryptoTransactions:
      return ['crypto_transactions'];
    case GQCanonicalDatasetKind.Packaging:
      return ['physical_goods_purchase'];
    case GQCanonicalDatasetKind.Vehicles:
      return [
        'vehicles',
        'non_building_electricity_consumption',
        'fuel_combustion',
      ];
    case GQCanonicalDatasetKind.Food:
      return ['food_mass', 'food_spend'];
    case GQCanonicalDatasetKind.SoldProductCircularity:
      return ['sold_product_circularity'];
    case GQCanonicalDatasetKind.EndOfLifeTreatmentOfSoldProducts:
    case GQCanonicalDatasetKind.EnergyAttributeCertificatesPurchased:
    case GQCanonicalDatasetKind.Factories:
    case GQCanonicalDatasetKind.WeightOfGoodsPurchased:
    case GQCanonicalDatasetKind.UseOfGoods:
    case GQCanonicalDatasetKind.UsePhaseOfGoods:
    case GQCanonicalDatasetKind.TextileProductSales:
    case GQCanonicalDatasetKind.Sites:
    case GQCanonicalDatasetKind.SupplierFactoriesTier1:
    case GQCanonicalDatasetKind.SupplierUpstreamLogistics:
    case GQCanonicalDatasetKind.Substances:
      // The above use 'custom' activity type
      // TODO: Create a new activity types
      return [];
    default:
      assertNever(canonicalDatasetKind);
  }
}

export function isDatasetKind(kind: string): kind is GQCanonicalDatasetKind {
  return isIncludedIn(kind, Object.keys(GQCanonicalDatasetKind));
}
